import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { useCustomerSearch } from "../../hooks/queries";
import { keysV2, useCreditPackages, useFeatures, useLedger, useWallet } from "../../hooks/v2";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import { formatDate, money } from "../../lib/format";
import type { Guardian } from "../../lib/types";
import type { CreditPackage } from "../../lib/typesV2";

const idem = () => `ui-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;

/** Cashless wallet: find a family, see cash and promo credits (with expiry), create the wallet, top up from a package or custom credits. */
export function WalletPanel() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast, toastError } = useToast();
  const qc = useQueryClient();
  const features = useFeatures();
  const [q, setQ] = useState("");
  const [family, setFamily] = useState<Guardian | null>(null);
  const found = useCustomerSearch(q);
  const enabled = features.data?.wallet_enabled !== false;
  const wallet = useWallet(enabled ? (family?.id ?? null) : null);
  const walletId = wallet.data?.wallet.id ?? null;
  const ledger = useLedger(walletId);
  const packages = useCreditPackages(enabled && !!walletId);
  const [pkg, setPkg] = useState("");
  const [credits, setCredits] = useState("");
  const [rate, setRate] = useState("100");
  const [busy, setBusy] = useState(false);

  const refresh = () => { if (family) void qc.invalidateQueries({ queryKey: keysV2.wallet(family.id) }); if (walletId) void qc.invalidateQueries({ queryKey: keysV2.ledger(walletId) }); };

  async function guarded(fn: () => Promise<void>) {
    setBusy(true);
    try { await fn(); refresh(); } catch (e) { toastError(e); } finally { setBusy(false); }
  }

  const create = () => guarded(async () => { await api.post("/wallets", { guardianId: family!.id }); });
  const topUp = () => guarded(async () => {
    const body = pkg ? { packageId: pkg, idempotencyKey: idem() } : { credits: Number(credits), filsPerCredit: Number(rate), idempotencyKey: idem() };
    const r = await api.post<{ balanceAfter: number }>(`/wallets/${walletId}/topup`, body);
    toast(t("wallet.added", { balance: r.balanceAfter }), "success");
    setPkg(""); setCredits("");
  });

  if (!enabled) return <div className="card p-5 text-sm font-bold text-gray-500">{t("wallet.disabled")}</div>;
  const w = wallet.data;
  const canTopUp = !!walletId && (pkg !== "" || (Number(credits) > 0 && Number(rate) >= 0));
  const pkgLabel = (p: CreditPackage) => t("wallet.packageLine", { name: locale === "ar" && p.name_ar ? p.name_ar : p.name, price: money(p.price_fils), credits: p.credits, bonus: p.bonus_credits });

  return (
    <div className="space-y-5">
      <div className="card flex items-center gap-3.5 p-5">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr from-cosmic-purple to-solar-orange text-2xl shadow-cosmic" aria-hidden>💳</div>
        <div>
          <h1 className="text-xl font-black tracking-tight">{t("wallet.title")}</h1>
          <p className="text-xs font-medium text-gray-500">{t("wallet.subtitle")}</p>
        </div>
      </div>

      <div className="card space-y-3 p-5">
        <input className="field" placeholder={t("wallet.search")} aria-label={t("wallet.search")} value={q} onChange={(e) => { setQ(e.target.value); setFamily(null); }} />
        {!family && (found.data ?? []).length > 0 && (
          <ul className="divide-y divide-gray-100 rounded-2xl border border-cosmic-silver">
            {found.data!.map((g) => (
              <li key={g.id}>
                <button type="button" className="flex w-full items-center justify-between px-4 py-2.5 text-start text-sm hover:bg-purple-50" onClick={() => { setFamily(g); setQ(g.fullName); }}>
                  <span className="font-black">{g.fullName}</span>
                  <span className="num font-mono text-xs text-gray-500">{g.phone}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {family && wallet.isSuccess && !w && (
        <div className="card flex items-center justify-between gap-3 p-5">
          <p className="text-sm font-bold text-gray-600">{t("wallet.noWallet")}</p>
          <button type="button" className="btn-primary" disabled={busy} onClick={create}>{t("wallet.create")}</button>
        </div>
      )}

      {w && (
        <>
          <div className="grid grid-cols-3 gap-3">
            {([["wallet.cash", w.wallet.cash_balance], ["wallet.promo", w.wallet.promo_balance], ["wallet.total", w.wallet.play_credits]] as const).map(([k, v]) => (
              <div key={k} className="card p-4 text-center">
                <div className="text-[10px] font-black uppercase tracking-wide text-gray-500">{t(k)}</div>
                <div className="num mt-1 text-2xl font-black text-cosmic-purple" data-testid={k}>{v}</div>
              </div>
            ))}
          </div>
          {w.expiringWithin30Days > 0 && <p role="status" className="rounded-xl bg-amber-50 p-3 text-xs font-bold text-amber-800">{t("wallet.expiring", { n: w.expiringWithin30Days })}</p>}

          <div className="card space-y-2 p-5">
            <h2 className="text-xs font-black uppercase tracking-wide">{t("wallet.lots")}</h2>
            <ul className="space-y-1.5 text-xs">
              {w.lots.map((l) => (
                <li key={l.id} className="flex items-center justify-between rounded-xl bg-gray-50 px-3 py-2">
                  <span><b className="num">{l.remaining_credits}</b> / <span className="num">{l.initial_credits}</span> • {t(l.kind === "cash" ? "wallet.cash" : "wallet.promo")}</span>
                  <span className="text-gray-500">{l.expires_at ? t("wallet.expires", { date: formatDate(l.expires_at, locale) }) : t("wallet.never")}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="card space-y-3 p-5">
            <h2 className="text-xs font-black uppercase tracking-wide">{t("wallet.topup")}</h2>
            <div>
              <label className="label" htmlFor="w-pkg">{t("wallet.package")}</label>
              <select id="w-pkg" className="field" value={pkg} onChange={(e) => setPkg(e.target.value)}>
                <option value="">—</option>
                {(packages.data ?? []).map((p) => <option key={p.id} value={p.id}>{pkgLabel(p)}</option>)}
              </select>
            </div>
            {!pkg && (
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label" htmlFor="w-credits">{t("wallet.customCredits")}</label><input id="w-credits" className="field num" inputMode="numeric" value={credits} onChange={(e) => setCredits(e.target.value.replace(/\D/g, ""))} /></div>
                <div><label className="label" htmlFor="w-rate">{t("wallet.rate")}</label><input id="w-rate" className="field num" inputMode="numeric" value={rate} onChange={(e) => setRate(e.target.value.replace(/\D/g, ""))} /></div>
              </div>
            )}
            <button type="button" className="btn-primary" disabled={!canTopUp || busy} onClick={topUp}>{busy ? t("common.loading") : t("wallet.confirm")}</button>
          </div>

          <div className="card space-y-2 p-5">
            <h2 className="text-xs font-black uppercase tracking-wide">{t("wallet.ledger")}</h2>
            <ul className="divide-y divide-gray-100 text-xs">
              {(ledger.data ?? []).map((e) => (
                <li key={e.id} className="flex items-center justify-between py-1.5">
                  <span className="font-bold">{e.type} <span className="num font-mono text-gray-400">{e.branch_code}</span></span>
                  <span className={`num font-mono font-black ${e.delta > 0 ? "text-emerald-600" : "text-red-600"}`}>{e.delta > 0 ? `+${e.delta}` : e.delta}</span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
